import {useUpdateUserLanguageMutation} from "@/api/userApi";
import ChevronIcon from "@/assets/colorable/chevron.svg?react";
import LanguageIcon from "@/assets/colorable/language.svg?react";
import TickIcon from "@/assets/colorable/tick.svg?react";
import ReusableDropdown from "@/components/Common/forms/ReusableDropdown";
import {Tooltip} from "@/components/Common/Tooltip";
import {useTheme} from "@/hooks/useTheme";
import {useIsLoggedIn} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {LANGUAGE_OPTIONS} from "@/store/constants";
import {languagePreferenceAtom, openTopBarPopoverAtom} from "@/store/jotaiAtoms";
import type {TLanguage} from "@/store/types";
import {useAtom, useSetAtom} from "jotai";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const LanguageSelector: FC<{
  onChange?: (language: TLanguage) => void;
  variant?: "dropdown" | "popover";
}> = ({onChange, variant = "popover"}) => {
  const {t, i18n} = useTranslation("common");
  const {resolvedTheme} = useTheme();
  const isLoggedIn = useIsLoggedIn();
  const setLanguagePreference = useSetAtom(languagePreferenceAtom);
  const [openPopover, setOpenPopover] = useAtom(openTopBarPopoverAtom);
  const {mutate: updateLanguage, isPending: isUpdatingLanguage} = useUpdateUserLanguageMutation();
  const value = LANGUAGE_OPTIONS.find(language => language.i18nextCode === i18n.resolvedLanguage)?.code;
  const options = LANGUAGE_OPTIONS.map(language => ({label: t(language.name), value: language.code}));

  const selectLanguage = (lang: TLanguage) => {
    if (!lang || value === lang) return;
    if (isLoggedIn) {
      updateLanguage({lang});
    } else {
      setLanguagePreference(lang);
    }
    onChange?.(lang);
  };

  if (variant === "dropdown") {
    return (
      <ReusableDropdown
        containerClassName="w-36"
        dropdownClassName="rounded-lg"
        isDisabled={isUpdatingLanguage}
        options={options}
        renderButton={({className, isOpen, selectedOption, ...rest}) => (
          <button
            {...rest}
            className={clsxm(
              className,
              "h-11 gap-2 rounded-lg text-sm",
              isOpen ? "border-primary-medium-solid" : "border-neutral-grey-light-dark",
            )}>
            <span className="text-neutral-black ml-2.5 grow text-left leading-none">{selectedOption?.label}</span>
            <ChevronIcon
              className={clsxm(
                "text-primary-dark-solid mr-2.5 size-2.5 shrink-0 transition-transform duration-250",
                isOpen && "rotate-180",
              )}
            />
          </button>
        )}
        renderItem={({className, isSelected, key, onClick, option, ...rest}) => (
          <li
            key={option.value}
            {...rest}
            className={clsxm(className, "hover:bg-neutral-white-dark h-10 gap-2 px-2.5 text-sm")}
            onClick={onClick}
            value={option.value}>
            <span className="text-neutral-black grow leading-none">{option.label}</span>
            {isSelected && <TickIcon className="text-primary-dark-solid" />}
          </li>
        )}
        selectedOption={options.find(option => option.value === value) ?? null}
        setSelectedOption={option =>
          selectLanguage(LANGUAGE_OPTIONS.find(language => language.code === option.value)?.code)
        }
      />
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <LanguageIcon
        data-tooltip-id="language-selector"
        className="text-primary-dark-solid size-5 shrink-0 cursor-pointer transition-all duration-150 hover:scale-[1.1] hover:opacity-75"
      />
      <Tooltip
        id="language-selector"
        isOpen={openPopover === "language"}
        setIsOpen={isOpen => setOpenPopover(prev => (isOpen ? "language" : prev === "language" ? null : prev))}
        openEvents={{click: true}}
        closeEvents={{click: true}}
        globalCloseEvents={{clickOutsideAnchor: true, escape: true}}
        clickable
        variant={resolvedTheme === "dark" ? "dark" : "light"}
        place="bottom-end"
        opacity={1}
        className="z-100 p-0 shadow-md"
        classNameArrow="shadow-md">
        <div className="flex flex-col divide-y">
          {LANGUAGE_OPTIONS.map(language => (
            <button
              key={language.code}
              disabled={isUpdatingLanguage}
              onClick={() => selectLanguage(language.code)}
              className={clsxm(
                "disabled:bg-neutral-white-dark disabled:text-neutral-black flex min-h-16 flex-col items-center justify-center gap-0.5 bg-white px-8 transition-all duration-150 disabled:cursor-not-allowed disabled:border-none",
                value === language.code ? "text-primary-dark-solid" : "text-neutral-black hover:text-neutral-black/80",
              )}>
              <span className="font-medium">{t(language.name)}</span>
            </button>
          ))}
        </div>
      </Tooltip>
    </div>
  );
};

export default LanguageSelector;
